import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Layers, Plus, Trash2, Pencil, Check, X, ShieldCheck } from 'lucide-react';
import AdminLayout from '../components/AdminLayout';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

/**
 * Feb 2026 — Zone Management CRUD (`/admin/zones`).
 *
 * Master list of warehouse zones. Backed by the `zones` collection
 * exposed via `/api/admin/warehouse/zones`. Gated on the fine-grained
 * `warehouse.zones` slug; super admin + `warehouse` module wildcard
 * implicitly pass. Consumed downstream by the Floor put-away dropdown
 * and the Live Stock Report zone chips.
 */
export default function AdminZones() {
  const authHeaders = useMemo(
    () => ({ Authorization: `Bearer ${localStorage.getItem('mr_admin_token') || ''}` }),
    [],
  );

  const [meLoaded, setMeLoaded] = useState(false);
  const [hasPerm, setHasPerm] = useState(false);
  const [warehouses, setWarehouses] = useState([]);
  const [zones, setZones] = useState([]);
  const [filterWh, setFilterWh] = useState('');
  const [busy, setBusy] = useState(false);
  const [form, setForm] = useState({ warehouse_id: '', code: '', name: '' });
  const [editingId, setEditingId] = useState(null);
  const [editForm, setEditForm] = useState({ warehouse_id: '', code: '', name: '' });

  useEffect(() => {
    (async () => {
      try {
        const me = await axios.get(`${API}/admin/me`, { headers: authHeaders });
        const superFlag = !!me.data?.is_super_admin;
        const perms = Array.isArray(me.data?.permissions) ? me.data.permissions : [];
        const wildcard = perms.includes('*') || perms.includes('warehouse');
        setHasPerm(superFlag || wildcard || perms.includes('warehouse.zones'));
      } catch { setHasPerm(false); }
      setMeLoaded(true);
    })();
  }, [authHeaders]);

  useEffect(() => {
    if (!hasPerm) return;
    axios
      .get(`${API}/admin/warehouses`, { headers: authHeaders })
      .then((r) => setWarehouses(r.data?.warehouses || r.data || []))
      .catch(() => setWarehouses([]));
  }, [hasPerm, authHeaders]);

  const load = async () => {
    if (!hasPerm) return;
    try {
      const r = await axios.get(`${API}/admin/warehouse/zones`, {
        params: filterWh ? { warehouse_id: filterWh } : {},
        headers: authHeaders,
      });
      setZones(r.data?.zones || []);
    } catch (e) {
      toast.error(String(e?.response?.data?.detail || 'Failed to load zones'));
    }
  };
  useEffect(() => { load(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [hasPerm, filterWh]);

  const whName = (id) => warehouses.find((w) => w.id === id)?.name || id;

  const createZone = async () => {
    if (!form.warehouse_id || !form.code.trim() || !form.name.trim()) {
      toast.error('Warehouse, code, and name are required');
      return;
    }
    setBusy(true);
    try {
      await axios.post(
        `${API}/admin/warehouse/zones`,
        { warehouse_id: form.warehouse_id, code: form.code.trim(), name: form.name.trim() },
        { headers: authHeaders },
      );
      setForm({ warehouse_id: form.warehouse_id, code: '', name: '' });
      toast.success('Zone created');
      await load();
    } catch (e) {
      toast.error(String(e?.response?.data?.detail || 'Create failed'));
    } finally { setBusy(false); }
  };

  const saveEdit = async (id) => {
    setBusy(true);
    try {
      await axios.patch(
        `${API}/admin/warehouse/zones/${id}`,
        editForm,
        { headers: authHeaders },
      );
      setEditingId(null);
      toast.success('Zone updated');
      await load();
    } catch (e) {
      toast.error(String(e?.response?.data?.detail || 'Update failed'));
    } finally { setBusy(false); }
  };

  const removeZone = async (id) => {
    if (!window.confirm('Delete this zone? Existing placements are not affected.')) return;
    setBusy(true);
    try {
      await axios.delete(`${API}/admin/warehouse/zones/${id}`, { headers: authHeaders });
      toast.success('Zone deleted');
      await load();
    } catch (e) {
      toast.error(String(e?.response?.data?.detail || 'Delete failed'));
    } finally { setBusy(false); }
  };

  if (!meLoaded) {
    return <AdminLayout><div className="p-8 text-slate-500 text-sm">Loading…</div></AdminLayout>;
  }
  if (!hasPerm) {
    return (
      <AdminLayout>
        <div className="p-8" data-testid="zones-forbidden">
          <div className="max-w-md mx-auto rounded-xl border-2 border-rose-200 bg-rose-50 p-6 text-center">
            <ShieldCheck className="w-8 h-8 text-rose-500 mx-auto mb-2" />
            <div className="font-bold text-rose-800">Not authorised</div>
            <div className="text-sm text-rose-700 mt-1">Ask a super admin to grant <code>warehouse.zones</code>.</div>
          </div>
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div className="p-6 max-w-5xl mx-auto" data-testid="zones-page">
        <div className="flex items-center gap-3 mb-4">
          <div className="w-10 h-10 rounded-xl bg-[#0078D4]/10 flex items-center justify-center">
            <Layers className="w-5 h-5 text-[#0078D4]" />
          </div>
          <div>
            <h1 className="text-xl font-bold text-slate-900">Zone Management</h1>
            <div className="text-xs text-slate-500">Master list per warehouse — feeds Floor put-away + Live Stock Report</div>
          </div>
        </div>

        {/* Add zone */}
        <div className="rounded-xl border-2 border-slate-200 bg-white p-5 mb-4">
          <div className="text-[10px] uppercase tracking-wider font-bold text-slate-500 mb-2">Add new zone</div>
          <div className="grid gap-2 sm:grid-cols-4 items-end">
            <label className="block sm:col-span-2">
              <span className="text-[11px] text-slate-500">Warehouse</span>
              <select
                value={form.warehouse_id}
                onChange={(e) => setForm({ ...form, warehouse_id: e.target.value })}
                className="mt-1 w-full h-11 rounded-lg border-2 border-slate-200 focus:border-[#0078D4] focus:outline-none px-3 text-sm bg-white"
                data-testid="zones-form-wh"
              >
                <option value="">— Select warehouse —</option>
                {warehouses.map((w) => (
                  <option key={w.id} value={w.id}>{w.name || w.id}</option>
                ))}
              </select>
            </label>
            <label className="block">
              <span className="text-[11px] text-slate-500">Code</span>
              <input
                value={form.code}
                onChange={(e) => setForm({ ...form, code: e.target.value })}
                placeholder="e.g. GROUND"
                className="mt-1 w-full h-11 rounded-lg border-2 border-slate-200 focus:border-[#0078D4] focus:outline-none px-3 text-sm font-mono"
                data-testid="zones-form-code"
              />
            </label>
            <label className="block">
              <span className="text-[11px] text-slate-500">Name</span>
              <input
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                placeholder="Ground Floor Racks"
                className="mt-1 w-full h-11 rounded-lg border-2 border-slate-200 focus:border-[#0078D4] focus:outline-none px-3 text-sm"
                data-testid="zones-form-name"
              />
            </label>
          </div>
          <div className="mt-3">
            <button
              onClick={createZone}
              disabled={busy}
              className="h-10 px-4 rounded-lg bg-[#0078D4] hover:bg-[#005FA3] text-white text-sm font-semibold disabled:bg-slate-300 disabled:cursor-not-allowed flex items-center gap-1"
              data-testid="zones-create-btn"
            >
              <Plus className="w-4 h-4" /> Add zone
            </button>
          </div>
        </div>

        {/* List */}
        <div className="rounded-xl border-2 border-slate-200 bg-white p-4">
          <div className="flex items-center justify-between mb-3">
            <div className="text-[10px] uppercase tracking-wider font-bold text-slate-500">Existing zones ({zones.length})</div>
            <label className="text-xs text-slate-500 flex items-center gap-2">
              Filter by warehouse
              <select
                value={filterWh}
                onChange={(e) => setFilterWh(e.target.value)}
                className="h-8 rounded-md border border-slate-200 px-2 text-xs bg-white"
                data-testid="zones-filter-wh"
              >
                <option value="">All</option>
                {warehouses.map((w) => (
                  <option key={w.id} value={w.id}>{w.name || w.id}</option>
                ))}
              </select>
            </label>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="zones-table">
              <thead className="bg-slate-50 text-slate-500 uppercase tracking-wider text-[10px]">
                <tr>
                  <th className="p-2 text-left">Warehouse</th>
                  <th className="p-2 text-left">Code</th>
                  <th className="p-2 text-left">Name</th>
                  <th className="p-2 w-24"></th>
                </tr>
              </thead>
              <tbody>
                {zones.length === 0 ? (
                  <tr><td colSpan={4} className="p-6 text-center text-xs text-slate-400">No zones yet.</td></tr>
                ) : zones.map((z) => (
                  <tr key={z.id} className="border-t border-slate-100 hover:bg-slate-50" data-testid={`zones-row-${z.id}`}>
                    {editingId === z.id ? (
                      <>
                        <td className="p-2">
                          <select value={editForm.warehouse_id} onChange={(e) => setEditForm({ ...editForm, warehouse_id: e.target.value })} className="w-full h-9 rounded border border-slate-200 px-2 text-xs bg-white">
                            {warehouses.map((w) => <option key={w.id} value={w.id}>{w.name || w.id}</option>)}
                          </select>
                        </td>
                        <td className="p-2"><input value={editForm.code} onChange={(e) => setEditForm({ ...editForm, code: e.target.value })} className="w-full h-9 rounded border border-slate-200 px-2 text-xs font-mono" /></td>
                        <td className="p-2"><input value={editForm.name} onChange={(e) => setEditForm({ ...editForm, name: e.target.value })} className="w-full h-9 rounded border border-slate-200 px-2 text-xs" /></td>
                        <td className="p-2">
                          <div className="flex gap-1 justify-end">
                            <button onClick={() => saveEdit(z.id)} disabled={busy} className="p-1.5 rounded-md bg-emerald-500 text-white" title="Save" data-testid="zones-save-btn"><Check className="w-3.5 h-3.5" /></button>
                            <button onClick={() => setEditingId(null)} className="p-1.5 rounded-md hover:bg-slate-100 text-slate-500" title="Cancel"><X className="w-3.5 h-3.5" /></button>
                          </div>
                        </td>
                      </>
                    ) : (
                      <>
                        <td className="p-2 text-slate-600">{whName(z.warehouse_id)}</td>
                        <td className="p-2 font-mono text-slate-900">{z.code}</td>
                        <td className="p-2 text-slate-700">{z.name}</td>
                        <td className="p-2">
                          <div className="flex gap-1 justify-end">
                            <button onClick={() => { setEditingId(z.id); setEditForm({ warehouse_id: z.warehouse_id, code: z.code, name: z.name }); }} className="p-1.5 rounded-md hover:bg-slate-100 text-slate-500" title="Edit" data-testid="zones-edit-btn"><Pencil className="w-3.5 h-3.5" /></button>
                            <button onClick={() => removeZone(z.id)} className="p-1.5 rounded-md hover:bg-rose-50 text-rose-500" title="Delete" data-testid="zones-delete-btn"><Trash2 className="w-3.5 h-3.5" /></button>
                          </div>
                        </td>
                      </>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </AdminLayout>
  );
}
